import './SdoAdminSetLimits.scss'
import { useTranslation } from 'react-i18next';
import AppSelect from '../AppSelect/AppSelect';
import { Button, Divider } from '@mui/material';
import AppNumber from '../AppNumber/AppNumber';
import { useEffect, useRef, useState } from 'react';
import { useSdoAdminEligibilityCriteriaProvider } from '../../providers/SdoAdminEligibilityCriteriaProvider';
import Loader from '../Loader/Loader';
import { SdoAvailabilityMonth } from '../../models/SdoInterface';
import { months } from '../../constants/dates';
import AcDialog from '../AcDialog/AcDialog';
import { Link } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { useSdoAdminReportsProvider } from '../../providers/SdoAdminReportsProvider';

interface Props {
    position?: any
}
export default function SdoAdminSetLimits({position}:Props) {
    const sdoAdminEligibilityCriteriaContext = useSdoAdminEligibilityCriteriaProvider();
    const sdoAdminReportsContext = useSdoAdminReportsProvider();
    const [selectedPosition, setSelectedPosition] = useState('');
    const { t } = useTranslation();
    const [sdoPositions, setSdoPositions] = useState<Array<string>>([]);
    const [sdoAvailabilityPerMonth, setSdoAvailabilityPerMonth] = useState<Array<SdoAvailabilityMonth>>([]);
    const [copiedValues, setCopiedValues] = useState<Array<SdoAvailabilityMonth>>([]);
    const selects = useRef([]);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [initialCurrentMonthLimit, setInitialCurrentMonthLimit] = useState(1);
    const [notificationRead, setNotificationRead] = useState(false);
    const [sdoMonthYear, setSdoMonthYear] = useState<any>({
        sdoMonth: '',
        sdoYear: ''
    });

    useEffect(() => {
        sdoAdminReportsContext.getSdoCurrentSubPeriodData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!sdoAdminReportsContext?.sdoCurrentSubPeriodData?.month) return;
        
        setSdoMonthYear({
            sdoMonth: sdoAdminReportsContext?.sdoCurrentSubPeriodData?.month,
            sdoYear: sdoAdminReportsContext?.sdoCurrentSubPeriodData?.year,
        });

    }, [sdoAdminReportsContext.sdoCurrentSubPeriodData]);

    useEffect(() => {
        if (!sdoAdminEligibilityCriteriaContext.sdoPositions.length) return;

        if(sdoMonthYear.sdoMonth && sdoMonthYear.sdoYear){    
            const positions = sdoAdminEligibilityCriteriaContext.sdoPositions.map(position => {
                return position.POSITION_GROUP;
            })
            if (positions.toString() === sdoPositions.toString()) return;
            setSdoPositions(positions);
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdoAdminEligibilityCriteriaContext.sdoPositions,sdoMonthYear])

    useEffect(() => {
        if (!sdoPositions.length) return
        if(position) {
            const pos = `${position.base}|${position.equipment}|${position.position}`;
            const posIndex = sdoPositions.findIndex(sdoPosition => {return sdoPosition === pos});
            if(posIndex !== -1) getPositionData(sdoPositions[posIndex]);
        } else {
            getPositionData(sdoPositions[0]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdoPositions])

    useEffect(() => {
        if (!sdoAdminEligibilityCriteriaContext.positionAvailabilityData.requestList.length) return;
        initializeSdoAvailabilityPerMonth();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdoAdminEligibilityCriteriaContext.positionAvailabilityData])

    useUpdateEffect(() => {
        if (!Object.keys(sdoAdminEligibilityCriteriaContext.manualUpdate).length) return;
        if (!sdoAdminEligibilityCriteriaContext.manualUpdate.value) saveLimits(false);
        else setShowSaveDialog(true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdoAdminEligibilityCriteriaContext.manualUpdate])

    useUpdateEffect(() => {
        if(!selectedPosition || !selects?.current['Position']?.querySelector('input')?.value) return;
        handleViewPositionClick();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPosition])

    const initializeSdoAvailabilityPerMonth = () => {
        const positionAvailabilityData = sdoAdminEligibilityCriteriaContext.positionAvailabilityData.requestList.map((data, index) => {
            return ({
                id: index.toString(),
                date: `${months[data.sdoMonth - 1]} ${data.sdoYear}`,
                availability: data.sdoAvailability,
                published: data.published
            })
        })
        setSdoAvailabilityPerMonth(positionAvailabilityData);
        setInitialCurrentMonthLimit(positionAvailabilityData[0].availability)
    }

    const getPositionData = (defaultPosition?) => {
        const positionProps = defaultPosition ? defaultPosition.replace(/\s/g, '').split('|') : selects.current['Position'].querySelector('input').value.replace(/\s/g, '').split('|');
        const position = {
            base: positionProps[0],
            equipment: positionProps[1],
            rank: positionProps[2]
        }
        setSelectedPosition(defaultPosition ? defaultPosition : selects.current['Position'].querySelector('input').value);
        sdoAdminEligibilityCriteriaContext.viewPosition(position,sdoMonthYear);
    }

    const handleViewPositionClick = () => {
        getPositionData();
        setNotificationRead(false);
        setShowNotification(false);
    }

    const handleUndoClick = () => {
        initializeSdoAvailabilityPerMonth();
    }

    const handleCopyValuesClick = () => {
        setCopiedValues(sdoAvailabilityPerMonth);
    }

    const handlePasteValuesClick = () => {
        if (!copiedValues.length) return;
        const checkPublishedValues = sdoAvailabilityPerMonth.map((value, index) => {
            if (!value.published) return copiedValues[index];
            return value
        })
        setSdoAvailabilityPerMonth(checkPublishedValues);
    }

    const handleSaveClick = () => {
        if (sdoAvailabilityPerMonth[0].availability === initialCurrentMonthLimit) {
            saveLimits(false);
        } else {
            checkCurrenMonthLimitChange();
        }
    }

    const checkCurrenMonthLimitChange = () => {
        const month = sdoMonthYear.sdoMonth;
        const year = sdoMonthYear.sdoYear;
        const positionProps = selects.current['Position'].querySelector('input').value.replace(/\s/g, '').split('|');
        const position = {
            base: positionProps[0],
            equipment: positionProps[1],
            rank: positionProps[2]
        }
        sdoAdminEligibilityCriteriaContext.checkCurrentMonthAvailabilityChanged(month, position, year, initialCurrentMonthLimit);
    }

    useUpdateEffect(() => {
        if (!sdoAdminEligibilityCriteriaContext.currentMonthLimitChanged.value || notificationRead) {
            const params = getSaveLimitsAPIParams();
            sdoAdminEligibilityCriteriaContext.checkCurrentMonthManualUpdateChanged(params.currentMonth, params.position, params.currentYear)
        } else {
            getPositionData(selectedPosition);
            setShowNotification(true);
        }

    }, [sdoAdminEligibilityCriteriaContext.currentMonthLimitChanged])

    const confirmSaveLimits = () => {
        saveLimits(true);
        handleClose();
    }

    const saveLimits = (reset: boolean) => {
        const params = getSaveLimitsAPIParams();
        sdoAdminEligibilityCriteriaContext.saveAvailabilities(params.currentMonth, params.position, params.currentYear, params.limits, reset);
    }

    const getSaveLimitsAPIParams = () => {
        const currentMonth = sdoMonthYear.sdoMonth;
        const currentYear =  sdoMonthYear.sdoYear;

        setShowNotification(false);
        const positionProps = selectedPosition.replace(/\s/g, '').split('|')
        const position = {
            base: positionProps[0],
            equipment: positionProps[1],
            rank: positionProps[2]
        }
        const limits = sdoAvailabilityPerMonth.map(month => {
            const monthData = month.date.split(' ')[0];
            const yearData = Number(month.date.split(' ')[1]);
            return ({
                sdoAvailability: Number(month.availability),
                sdoMonth: months.findIndex(m => m === monthData) + 1,
                sdoYear: yearData
            })
        })
        return {
            currentMonth: currentMonth,
            position: position,
            currentYear: currentYear,
            limits: limits
        }

    }

    const popup = () => {
        const date = new Date();
        const currentMonthNumber = date.getMonth();
        const monthName = months[currentMonthNumber];
        const currentYear = date.getFullYear();
        const positionProps = selectedPosition.replace(/\s/g, '').split('|');
        return {
            icon: 'icon-circle-flight',
            title: t('specialDayOff.adminEligibilityCriteria.setLimits.popup.title'),
            description: `Updating the SDO Availability will reset and remove any manual decisions to the <br> ${monthName} ${currentYear} - <b> ${positionProps[0]} | ${positionProps[1]} | ${positionProps[2]} </b> report by administrators.`,
            actionLabel: t('specialDayOff.adminEligibilityCriteria.setLimits.popup.cancel'),
            handleAction: handleClose,
            secondaryActionLabel: t('specialDayOff.adminEligibilityCriteria.setLimits.popup.confirm'),
            handleSecondaryAction: confirmSaveLimits,
        }
    }

    const handleClose = () => {
        setShowSaveDialog(false);
    }

    const handleChange = (event) => {
        let value = parseInt(event.target.value);
        value = (value && value >=0 && value <= 99) ? value : 0;
        setSdoAvailabilityPerMonth(prevState => {
            const newState = prevState.map(obj => { 
                if (obj.id === event.target.id) {
                    return { ...obj, availability: value}
                }
                return obj
            })
            return newState;
        })
    }

    return (
        sdoAdminEligibilityCriteriaContext.limitsLoading ? <Loader /> :
            <div className='sdo-admin-ec-set-limits-cntr'>
                <span className='set-limits__title'>{t('specialDayOff.adminEligibilityCriteria.setLimits.title')}</span>
                <div className='set-limits__notification-cntr'>
                    <i className='icon-info-warning set-limits-icon'></i>
                    <span>{t('specialDayOff.adminEligibilityCriteria.setLimits.infoMessage')}</span>
                </div>
                <div className='set-limits__position-inputs-cntr'>
                    <div className='select-cntr--pos'>
                        <AppSelect
                            fillElements={sdoPositions}
                            multiSelect={false}
                            selectedItem={selectedPosition}
                            selectLabel='Position'
                            selectRef={selects}
                            setSelectedParent={setSelectedPosition}
                        />
                    </div>
                    <Button variant='outlined' className='btn-primary' onClick={() => handleViewPositionClick()}>{t('specialDayOff.adminEligibilityCriteria.setLimits.viewPosition')}</Button>
                </div>
                <Divider className='list-separator' />
                <div className='set-limits__values-cntr'>
                    {showNotification && 
                    <div className='notification notification--limits'> 
                        <i className='icon-circle-flight'></i>
                        {t('specialDayOff.adminEligibilityCriteria.setLimits.notificationA')} <b> &nbsp;{sdoAvailabilityPerMonth[0]?.date.split(' ')[0]}&nbsp; </b> {t('specialDayOff.adminEligibilityCriteria.setLimits.notificationB')}
                        <span className='icon-close icon-close-right' onClick={() => {
                        setShowNotification(false);
                        setNotificationRead(true);
                        }}></span>
                    </div>}
                    <div className='values__header'>
                        <div className='values__header__sub-cntr'>
                            <span className='values__header__position--lbl'>{t('specialDayOff.adminEligibilityCriteria.setLimits.position')}:</span>
                            <span className='values__header__position'>{selectedPosition}</span>
                            <Link 
                                to={'/sdo-admin-report-details'}
                                state={{position: selectedPosition}}
                                className='values__header__reports-link'>
                                <span className='text-link text-link--view-reports'>{t('specialDayOff.adminEligibilityCriteria.manageRequests.viewRequests')}</span>
                            </Link>
                        </div>
                        <div className='values__header__sub-cntr'>
                            <i className='icon-copy icon--x-large' onClick={() => handleCopyValuesClick()} />
                            <span className='text-link text-link--values text-link--values__copy' onClick={() => handleCopyValuesClick()}>{t('specialDayOff.adminEligibilityCriteria.setLimits.copyValues')}</span>
                            <i className='icon-paste icon--x-large' onClick={() => handlePasteValuesClick()} />
                            <span className='text-link text-link--values' onClick={() => handlePasteValuesClick()}>{t('specialDayOff.adminEligibilityCriteria.setLimits.pasteValues')}</span>
                        </div>
                    </div>
                    {showSaveDialog && <AcDialog open={showSaveDialog} onClose={handleClose} data={popup()} />}
                    <div className='values__inputs-cntr'>
                        {sdoAvailabilityPerMonth.map((month, index) => {
                            return (
                                <div className='input__inner-cntr' key={index}>
                                    <p className='input__lbl'>{month.date}</p>
                                    <AppNumber
                                        label={'Available Per Day'}
                                        inputProps={{
                                            disableUnderline: true,
                                            inputProps: { min: 0, max: 99 }
                                        }}
                                        defaultValue={month.availability}
                                        handleOnChange={handleChange}
                                        id={index}
                                        disabled={month.published}
                                    />
                                </div>
                            )
                        })}
                    </div>
                    <div className='save-cancel-btn-cntr'>
                        <Button variant='outlined' className='btn-primary btn-primary--cancel' onClick={handleUndoClick}>{t('specialDayOff.adminEligibilityCriteria.setLimits.undoChanges')}</Button>
                        <Button variant='outlined' className='btn-primary btn-primary--save' onClick={handleSaveClick}>{t('specialDayOff.adminEligibilityCriteria.setLimits.save')}</Button>
                    </div>
                </div>
            </div>
    )
}